import React, { useCallback, useContext, useState } from 'react'
import { useCookies } from 'react-cookie'
import { Popup, Loader, Icon, Item } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'

import { useAppSelector } from 'redux/hooks'
import { selectSaltSum } from 'redux/deck/selectors'

import { SaltResponseType } from 'types/salt'
import { EDH_FORMATS } from 'types/deck'

import RequestService from 'services/request.service'

import SaltDistribution from 'components/deckPage/header/SaltDistribution'
import SaltSumInfo from 'components/tooltips/SaltSumInfo'

import styles from './saltSum.module.scss'

type Props = Record<string, never>

const SALT_DISTRIBUTION_URL = 'https://storage.googleapis.com/topdekt-user/salt/salt_distribution.json'

/**
 * This component renders the deck meta data of the salt sum as a trigger for a semantic Popup component. This popup
 * contains the charts of the salt distributions on Archidekt.
 *
 * In order to only fetch the distribution data as needed, it is fetched on hover of the popup. A loader is rendered
 * in place of the charts while the distribution data is being loaded.
 */
const SaltSum: React.FC<Props> = () => {
  const saltSum = useAppSelector(selectSaltSum)
  const format = useAppSelector(state => state.deck.format)

  const theme = useContext(ThemeContext)

  const [averageData, setAverageData] = useState<SaltResponseType | null>(null)
  const [averageLoading, setAverageLoading] = useState(false)

  const [cookies, _setCookie] = useCookies(['saltHidden'])
  const saltHidden = cookies.saltHidden === 'true'

  const handleHover = useCallback(() => {
    if (!averageData && !averageLoading) {
      setAverageLoading(true)

      RequestService.get(SALT_DISTRIBUTION_URL, {}, true)
        .then(data => setAverageData(data))
        .catch(() => console.error('Unable to fetch Salt Distribution data.'))
        .finally(() => setAverageLoading(false))
    }
  }, [])

  if (!EDH_FORMATS.includes(format)) return null

  return (
    <Item.Meta style={{ whiteSpace: 'nowrap' }}>
      {!saltHidden && (
        <>
          Salt sum:&nbsp;
          <Popup
            position="bottom left"
            offset={[-40, 0]}
            style={{ backgroundColor: theme?.lightBackground, background: theme?.lightBackground }}
            content={
              !averageData ? (
                <Loader style={{ zIndex: 0 }} active inline size="tiny" />
              ) : (
                <SaltDistribution sum={saltSum} averageData={averageData} />
              )
            }
            trigger={
              <span onMouseEnter={handleHover} onClick={handleHover} className={styles.orange}>
                {saltSum}
              </span>
            }
          />
          <SaltSumInfo />
        </>
      )}
    </Item.Meta>
  )
}

export default SaltSum
